import ApexCharts from "apexcharts";
import React, { useEffect } from "react";

import { ReportTargetType, useGetSessionTargetProgressQuery } from "@api/graphql/types-and-hooks";
import {
  ChartSeries,
  EventAnnotation,
  PhaseAnnotation,
  SocialInteractionChart,
} from "@components/Charts/SocialInteractionChart";
import { useAdminData } from "@providers/AdminDataProvider";

interface TargetProgressGraphProps {
  reportGoalId: string;
  target: Pick<ReportTargetType, "id" | "summaryReviewed"> | null | undefined;
  signedChartImageUrl: string;
  forClientProgramming?: boolean;
  chartRef?: React.RefObject<ApexCharts | null>;
  loaderClassName?: string;
  /** Fires once the session-progress query settles; mirrors ShortTermGoalProgressGraph's onSelectedChartReady. */
  onReady?: (ready: boolean) => void;
}

export const TargetProgressGraph = ({
  reportGoalId,
  target,
  signedChartImageUrl,
  forClientProgramming,
  chartRef,
  loaderClassName = "",
  onReady,
}: TargetProgressGraphProps) => {
  const { isCentralReachUser } = useAdminData();
  const { data, isLoading } = useGetSessionTargetProgressQuery(
    {
      filter: {
        goalId: reportGoalId,
        targetId: target?.id,
      },
    },
    {
      enabled: !!reportGoalId && !!target?.id,
      retry: false,
    },
  );

  useEffect(() => {
    onReady?.(!isLoading);
  }, [isLoading, onReady]);

  const targetProgress = data?.getSessionProgress?.progress?.find((progress) => progress?.targetId === target?.id);

  const series: ChartSeries[] = targetProgress
    ? [
        {
          name: targetProgress.targetName || "",
          data:
            targetProgress.data?.map((d) => ({
              x: new Date(d?.date).getTime(),
              y: d?.value ?? 0,
            })) || [],
        },
      ]
    : [];

  const annotations: PhaseAnnotation[] =
    targetProgress?.phases?.map((phase, index, arr) => {
      const nextPhase = arr[index + 1];
      return {
        label: phase?.status || "",
        position: new Date(phase?.date).getTime(),
        position2: nextPhase ? new Date(nextPhase.date).getTime() : undefined,
        area: true,
        fillColor: phase?.status === "Baseline" ? "#00BFFF" : phase?.status === "Inactive" ? "#708090" : "#90EE90",
      };
    }) || [];

  const eventAnnotations: EventAnnotation[] =
    (targetProgress?.events?.map((event) => {
      // truncate the label text if it is too long
      const eventText = event?.text && event?.text?.length > 30 ? `${event?.text?.substring(0, 30)}...` : event?.text;
      return {
        label: eventText,
        position: new Date(event?.date).getTime(),
      };
    }) as EventAnnotation[]) || [];

  const chartId = `target_${target?.id}_progress`;

  return !forClientProgramming && target?.summaryReviewed && signedChartImageUrl ? (
    <div className="p-4 bg-white">
      <img src={signedChartImageUrl} alt="Invalid Chart Url" />
    </div>
  ) : (
    <SocialInteractionChart
      isLoading={isLoading}
      chartId={chartId}
      series={series}
      phaseAnnotations={annotations}
      eventAnnotations={eventAnnotations}
      progressType={targetProgress?.progressType || ""}
      masteryLabel="Mastery Goal"
      key={2}
      forClientProgramming={forClientProgramming}
      chartRef={chartRef}
      loaderClassName={loaderClassName}
      breakAtPhaseChange={isCentralReachUser}
    />
  );
};
